import React, { useMemo, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { RadioGroup, FieldWrapper } from 'common/form';
import { Input } from 'components/Inputs';
import {
  CLOSE_CONVERSATION_OPTIONS,
  CLOSE_CONVERSATION_CUSTOM_VALUE
} from 'modules/campaign-creation/constants/closeConversationOptions';
import { convertDaysToHours, convertHoursToDays } from 'utils/datetime';

type Props = {
  timeToAutoCloseConversationsInHours: number | undefined;
  setTimeToAutoCloseConversationsInHours: (value: number | undefined) => void;
  isEdit: boolean | undefined;
  error?: string;
};

export const CloseConversationTimeSelector: React.FC<Props> = ({
  timeToAutoCloseConversationsInHours,
  setTimeToAutoCloseConversationsInHours,
  isEdit,
  error
}) => {
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  useEffect(() => {
    if (timeToAutoCloseConversationsInHours) {
      const hourString = String(timeToAutoCloseConversationsInHours);
      if (!['24', '48', '168'].includes(hourString)) {
        setIsCustomSelected(true);
      }
    }
  }, []);

  const radioValue = useMemo(() => {
    if (isCustomSelected) {
      return CLOSE_CONVERSATION_CUSTOM_VALUE;
    }

    if (!timeToAutoCloseConversationsInHours) {
      return CLOSE_CONVERSATION_CUSTOM_VALUE;
    }

    const hourString = String(timeToAutoCloseConversationsInHours);
    if (['24', '48', '168'].includes(hourString)) {
      return hourString;
    }

    return CLOSE_CONVERSATION_CUSTOM_VALUE;
  }, [timeToAutoCloseConversationsInHours, isCustomSelected]);

  const onChange = (value: string) => {
    if (value === CLOSE_CONVERSATION_CUSTOM_VALUE) {
      setIsCustomSelected(true);
      setTimeToAutoCloseConversationsInHours(undefined);
      return;
    }

    setIsCustomSelected(false);
    setTimeToAutoCloseConversationsInHours(parseInt(value));
  };

  const onCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      setTimeToAutoCloseConversationsInHours(undefined);
      return;
    }
    const valueInHours = convertDaysToHours(parseInt(inputValue));
    setTimeToAutoCloseConversationsInHours(valueInHours);
  };

  return (
    <Box>
      <RadioGroup
        name="closeConversationAfter"
        value={radioValue}
        field={{
          label: 'Close conversations after',
          subLabel: 'If candidates do not respond.',
          options: CLOSE_CONVERSATION_OPTIONS,
          disabled: isEdit
        }}
        onChange={onChange}
      />
      {radioValue === CLOSE_CONVERSATION_CUSTOM_VALUE && (
        <Box sx={{ mt: '20px', maxWidth: '200px' }}>
          <FieldWrapper label="Custom delay (days)" errorMessage={error}>
            <Input
              type="number"
              InputProps={{
                inputProps: { min: 1, step: 1 }
              }}
              onChange={onCustomChange}
              value={
                timeToAutoCloseConversationsInHours !== undefined
                  ? convertHoursToDays(timeToAutoCloseConversationsInHours)
                  : ''
              }
              disabled={isEdit}
            />
          </FieldWrapper>
        </Box>
      )}
    </Box>
  );
};
